import { type ReactElement, useCallback, useState } from 'react';
import { Checkbox, Divider, Popover } from 'antd';

const POPOVER_ALIGN = { offset: [0, -8] };

export interface FieldSelectOption {
    key: string;
    label: string;
}

export interface FieldSelectPopoverProps {
    /** Heading above the option list, e.g. "Select KPIs". */
    title: string;
    options: FieldSelectOption[];
    selected: string[];
    /**
     * Called once with the final selection when the popover closes. Deferring the
     * commit keeps the grid from refetching on every individual checkbox click.
     */
    onCommit: (next: string[]) => void;
    selectAllLabel: string;
    deselectAllLabel: string;
    /** The trigger element the popover anchors to. */
    children: ReactElement;
}

/**
 * Checkbox multi-select in a popover, with a tri-state select-all. Used for
 * column/KPI visibility pickers in TPO Gen toolbars.
 */
const FieldSelectPopover = ({
    title,
    options,
    selected,
    onCommit,
    selectAllLabel,
    deselectAllLabel,
    children,
}: FieldSelectPopoverProps) => {
    const [draft, setDraft] = useState<string[]>(selected);

    const allSelected = options.length > 0 && options.every((option) => draft.includes(option.key));
    const someSelected = !allSelected && options.some((option) => draft.includes(option.key));

    const handleSelectAll = useCallback(() => {
        setDraft(allSelected ? [] : options.map((option) => option.key));
    }, [allSelected, options]);

    const handleToggle = useCallback((key: string) => {
        setDraft((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]));
    }, []);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            // Re-seed from the committed selection on open so a cancelled edit
            // (closing without changes taking effect elsewhere) cannot drift.
            if (open) setDraft(selected);
            else onCommit(options.filter((option) => draft.includes(option.key)).map((option) => option.key));
        },
        [selected, draft, options, onCommit],
    );

    return (
        <Popover
            trigger="click"
            showArrow={false}
            placement="bottomRight"
            align={POPOVER_ALIGN}
            onOpenChange={handleOpenChange}
            content={
                <div className="py-3">
                    <p className="text-[#807E86] text-xs">{title}</p>
                    <Divider className="!my-[0.3125rem]" />
                    <div className="flex max-h-[18rem] flex-col items-start overflow-y-auto">
                        <div className="px-3 py-[0.3125rem]">
                            <Checkbox checked={allSelected} indeterminate={someSelected} onChange={handleSelectAll}>
                                <p className="text-sm">{allSelected ? deselectAllLabel : selectAllLabel}</p>
                            </Checkbox>
                        </div>
                        {options.map((option) => (
                            <div className="px-3 py-[0.3125rem]" key={option.key}>
                                <Checkbox
                                    checked={draft.includes(option.key)}
                                    onChange={() => handleToggle(option.key)}
                                >
                                    <p className="text-sm">{option.label}</p>
                                </Checkbox>
                            </div>
                        ))}
                    </div>
                </div>
            }
        >
            {children}
        </Popover>
    );
};

FieldSelectPopover.displayName = 'FieldSelectPopover';

export default FieldSelectPopover;
